import { useState } from 'react';
import { useAccessRegistry, useCatalog, useCreateProposal, useFeedbackList, useIncomingSecrets, useOffboard, useRevokeAccess, useSendForSignature, useStudioProposals, useStudioSignRequests } from '@/api/studio';
import type { Client, Project, Proposal, SignKind, SignRequest } from '@/api/types';
import { ConfirmButton } from '@/components/ConfirmDialog';
import { SignatureView } from '@/components/SignDialog';
import { Dialog, Status } from '@/components/ui';
import { SECRET_NOTICE } from '@/lib/access';
import { useStudioTerms } from '@/lib/contour';
import { formatMoney, formatWhen } from '@/lib/format';
import { secretStatus } from '@/lib/status';
import { OpenOnce } from './OpenOnce';
import { hasOpenedValue } from './openedValues';

const kpStatus = { draft: ['neutral', 'Черновик'], sent: ['waiting', 'Отправлено'], accepting: ['active', 'Принимается'], accepted: ['done', 'Принято'], declined: ['neutral', 'Отказ'], withdrawn: ['neutral', 'Отозвано'], lost: ['neutral', 'Закрыто как отказ'] } as const;

function NewProposal({ client, onClose }: { client: Client; onClose: () => void }) {
  const { data: catalog } = useCatalog();
  const create = useCreateProposal();
  const { currency } = useStudioTerms();
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [milestones, setMilestones] = useState<Proposal['milestones']>([
    { title: 'Структура', weeks: 1 },
    { title: 'Дизайн', weeks: 2 },
    { title: 'Запуск', weeks: 1 },
  ]);
  // A whole percent from 0 to 100, typed freely; the bff checks it again.
  const [preText, setPreText] = useState('50');
  const pre = Number(preText);
  const preOk = preText !== '' && Number.isInteger(pre) && pre >= 0 && pre <= 100;
  const total = catalog?.filter((c) => picked.includes(c.id)).reduce((n, c) => n + c.price.amountMinor, 0) ?? 0;
  const money = catalog?.[0]?.price.currency ?? currency;
  const kind = catalog?.find((c) => picked.includes(c.id))?.kind === 'subscription' ? 'retainer' : 'fixed';

  return (
    <Dialog open onClose={onClose} title={`Предложение для «${client.name}»`} wide>
      <form
        className="space-y-4 p-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!title.trim() || !picked.length || !preOk) return;
          create.mutate({ clientId: client.id, title: title.trim(), intro: intro.trim(), kind, catalogIds: picked, milestones: milestones.filter((m) => m.title.trim()), prepaymentPercent: pre, validDays: 14 }, { onSuccess: onClose });
        }}
      >
        <input className="field text-md" placeholder="Что предлагаем — так клиент увидит заголовок" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        <textarea className="field min-h-24" placeholder="Зачем это клиенту и какой будет результат, 2–3 предложения" value={intro} onChange={(e) => setIntro(e.target.value)} />
        <fieldset>
          <legend className="mb-1.5 text-sm text-muted">Услуги из каталога</legend>
          <div className="flex flex-wrap gap-1.5">
            {catalog?.map((c) => (
              <button key={c.id} type="button" className="chip" aria-pressed={picked.includes(c.id)} onClick={() => setPicked((p) => (p.includes(c.id) ? p.filter((x) => x !== c.id) : [...p, c.id]))}>
                {c.title}, {formatMoney(c.price)}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend className="mb-1.5 text-sm text-muted">План</legend>
          <div className="space-y-2">
            {milestones.map((m, i) => (
              <div key={i} className="grid grid-cols-[1fr_100px] gap-2">
                <input className="field" value={m.title} onChange={(e) => setMilestones((ms) => ms.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
                <input className="field num" type="number" min={1} value={m.weeks} onChange={(e) => setMilestones((ms) => ms.map((x, j) => (j === i ? { ...x, weeks: Number(e.target.value) } : x)))} aria-label="Недель" />
              </div>
            ))}
            <button type="button" className="btn-ghost" onClick={() => setMilestones((ms) => [...ms, { title: '', weeks: 1 }])}>
              Ещё этап
            </button>
          </div>
        </fieldset>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
          <div className="text-sm text-muted">
            <label className="flex items-center gap-2">
              Предоплата
              <input
                className="field num h-8 w-20 py-0 text-right"
                inputMode="numeric"
                aria-label="Предоплата, %"
                aria-invalid={!preOk}
                value={preText}
                onChange={(e) => setPreText(e.target.value.replace(/\D/g, '').slice(0, 3))}
              />
              %
              {preOk && (
                <span className="num" data-testid="prepayment-amount">
                  = {formatMoney({ amountMinor: Math.floor((total * pre) / 100), currency: money })}
                </span>
              )}
            </label>
            {!preOk && (
              <p className="mt-1 text-xs text-overdue" role="alert">
                Целое число от 0 до 100
              </p>
            )}
          </div>
          <span className="num font-semibold">{formatMoney({ amountMinor: total, currency: money })}</span>
          <button className="btn-primary" disabled={!title.trim() || !picked.length || !preOk || create.isPending}>
            Отправить клиенту
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function Proposals({ client }: { client: Client }) {
  const { data } = useStudioProposals(client.id);
  const [creating, setCreating] = useState(false);
  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold">Предложения</h2>
        <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setCreating(true)}>
          Новое КП
        </button>
      </div>
      {data?.length ? (
        <ul className="divide-y divide-line border-y border-line">
          {data.map((p) => {
            const [tone, label] = kpStatus[p.status];
            const stale = p.status === 'sent' && new Date(p.validUntil).getTime() < Date.now();
            return (
              <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2.5 text-sm">
                <span className="flex-1 max-sm:basis-full">
                  {p.number} {p.title}
                  <span className="block text-xs text-muted">
                    {p.sentAt && `отправлено ${formatWhen(p.sentAt)}`}
                    {p.declineReason && `, причина: ${p.declineReason}`}
                    {stale && ', срок истёк — клиент молчит'}
                  </span>
                </span>
                <span className="num">{formatMoney(p.total)}</span>
                <Status tone={stale ? 'overdue' : tone} className="ml-auto sm:w-28 sm:justify-end">
                  {stale ? 'Тишина' : label}
                </Status>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-muted">Предложений не было.</p>
      )}
      {creating && <NewProposal client={client} onClose={() => setCreating(false)} />}
    </section>
  );
}

export function Signatures({ client, projects }: { client: Client; projects: Project[] }) {
  const { data } = useStudioSignRequests();
  const send = useSendForSignature();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<SignKind>('addendum');
  const { acts } = useStudioTerms();
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState('');
  const [file, setFile] = useState<File>();
  const [viewing, setViewing] = useState<SignRequest>();
  const ids = new Set(projects.map((p) => p.id));
  const mine = client.id === 'karandash' ? data : data?.filter((s) => s.projectId && ids.has(s.projectId));

  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold">Документы на подпись</h2>
        <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setOpen(true)}>
          Отправить документ
        </button>
      </div>
      <ul className="divide-y divide-line border-y border-line">
        {mine?.map((s) => (
          <li key={s.id} className="flex items-center gap-4 py-2.5 text-sm">
            <span className="flex-1">
              <button type="button" className="text-left underline-offset-2 hover:underline" onClick={() => setViewing(s)}>
                {s.title}
              </button>
              <span className="block text-xs text-muted">{s.status === 'signed' ? `подписал ${s.signedBy}, ${formatWhen(s.signedAt!)}, ${s.hash}` : `отправлен ${formatWhen(s.createdAt)}`}</span>
            </span>
            <Status tone={s.status === 'signed' ? 'done' : 'waiting'}>{s.status === 'signed' ? 'Подписан' : 'Ждёт подписи'}</Status>
          </li>
        ))}
      </ul>
      {viewing && <SignatureView studio request={viewing} onClose={() => setViewing(undefined)} />}
      <Dialog open={open} onClose={() => setOpen(false)} title="Отправить на подпись">
        <form
          className="space-y-3 p-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!title.trim() || !file) return;
            send.mutate({ clientId: client.id, kind, title: title.trim(), file, projectId: projectId || undefined }, { onSuccess: () => (setOpen(false), setTitle(''), setFile(undefined)) });
          }}
        >
          <div className="flex gap-1.5">
            {(
              [
                ['contract', 'Договор'],
                ['addendum', 'Допсоглашение'],
                ['act', 'Акт'],
              ] as const
            )
              // Acts are RU paperwork; the main contour has none.
              .filter(([v]) => acts || v !== 'act')
              .map(([v, l]) => (
              <button key={v} type="button" className="chip" aria-pressed={kind === v} onClick={() => setKind(v)}>
                {l}
              </button>
            ))}
          </div>
          <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Название документа" />
          <select className="field" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Без проекта</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
          <label className="block text-sm">
            <span className="mb-1.5 block text-muted">Файл PDF, до 20 МБ</span>
            <input type="file" accept="application/pdf" className="field" onChange={(e) => setFile(e.target.files?.[0])} />
          </label>
          {send.error && <p className="text-sm text-overdue">{(send.error as Error).message}</p>}
          <p className="text-xs text-muted">Клиент откроет этот файл перед подписью; отпечаток файла сохранится в записи подписи. Подпись простая ЭП по договору.</p>
          <button className="btn-primary w-full" disabled={!title.trim() || !file || send.isPending}>
            Отправить
          </button>
        </form>
      </Dialog>
    </section>
  );
}

export function AccessRegistry({ client }: { client: Client }) {
  const { data } = useAccessRegistry(client.id);
  const { data: mine } = useIncomingSecrets();
  const revoke = useRevokeAccess();
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold">Доступы, переданные через кабинет</h2>
      {data?.length ? (
        <ul className="divide-y divide-line border-y border-line">
          {data.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-x-4 py-2.5 text-sm">
              <span className="flex-1">
                {s.title}
                <span className="block text-xs text-muted">
                  {s.from.name} → {s.to.name}, {formatWhen(s.createdAt)}
                </span>
                {((s.status === 'waiting' && mine?.some((m) => m.id === s.id)) || hasOpenedValue(s.id)) && <OpenOnce id={s.id} title={s.title} from={s.from.name} />}
              </span>
              <Status tone={secretStatus[s.status].tone}>{secretStatus[s.status].label}</Status>
              {s.status === 'waiting' && (
                <ConfirmButton label="Отозвать" className="btn-ghost h-7 px-2 text-xs" title="Отозвать доступ?" confirmLabel="Отозвать" quiet onConfirm={() => revoke.mutate(s.id)}>
  Доступ «{s.title}» перестанет открываться. Если получатель его уже открыл, значения у себя нужно сменить.
</ConfirmButton>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted">Через кабинет доступы не передавались.</p>
      )}
      <p className="mt-2 text-xs text-muted">{SECRET_NOTICE} Открытые доступы — это уже знание получателя: при завершении работы их меняют у себя в сервисах. Кабинет напомнит список.</p>
    </section>
  );
}

export function FeedbackSummary() {
  const { data } = useFeedbackList();
  if (!data?.length) return null;
  const avg = data.reduce((n, f) => n + f.score, 0) / data.length;
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold">Оценки клиента</h2>
      <p className="num text-lg font-semibold">{avg.toFixed(1)} из 10</p>
      <ul className="mt-2 space-y-2 text-sm">
        {data.map((f) => (
          <li key={f.id}>
            <span className="num font-medium">{f.score}</span> <span className="text-muted">{f.context}, {formatWhen(f.at)}</span>
            {f.comment && <p className="text-muted">«{f.comment}»</p>}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function OffboardDialog({ client, onClose }: { client: Client; onClose: () => void }) {
  const offboard = useOffboard();
  const { acts } = useStudioTerms();
  const { data: access } = useAccessRegistry(client.id);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const steps = [
    acts ? 'Все счета закрыты, итоговый акт подписан' : 'Все счета закрыты и оплачены',
    'Клиенту переданы исходники, доступы к сайтам и рекламным кабинетам',
    'Доступы студии в кабинетах клиента удалены или переданы',
    'Клиент получил список доступов, которые стоит сменить у себя',
    'Попросили отзыв и разрешение на кейс',
  ];
  const opened = access?.filter((s) => s.status === 'opened') ?? [];
  const all = steps.every((s) => checked[s]);
  return (
    <Dialog open onClose={onClose} title={`Завершить работу с «${client.name}»`} wide>
      <div className="space-y-4 p-5">
        <ul className="space-y-2">
          {steps.map((s) => (
            <li key={s}>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-0.5" checked={!!checked[s]} onChange={(e) => setChecked((c) => ({ ...c, [s]: e.target.checked }))} /> {s}
              </label>
            </li>
          ))}
        </ul>
        {opened.length > 0 && (
          <div className="rounded bg-panel p-3 text-sm shadow-hair">
            <div className="font-medium">Сменить после завершения</div>
            <ul className="mt-1 list-disc pl-4 text-muted">
              {opened.map((s) => (
                <li key={s.id}>{s.title}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-xs text-muted">Неоткрытые доступы будут отозваны. Клиент останется в архиве, кабинет перейдёт в режим «только чтение».</p>
        <button className="btn-primary w-full" disabled={!all || offboard.isPending} onClick={() => offboard.mutate(client.id, { onSuccess: onClose })}>
          Завершить работу
        </button>
      </div>
    </Dialog>
  );
}
